import { Theme } from "@emotion/react";
import { CSSObject } from "@emotion/styled";
import { VisibleFocusStyle } from "@kaltura/ds-react-theme";

/**
 * object containing general HTML styles according to the Design-System.
 * should be used to apply DS-styles to user-generated texts that have HTML content,
 * like descriptions, etc.
 * Usage example:
 * <pre>
 * const StyledUserGeneratedHtmlText = styled("div")(({ theme }) => [
 *     generalHtmlStyles(theme),
 *     {
 *         // other styles
 *     },
 * ]);
 *
 * ....
 *
 * <StyledUserGeneratedHtmlText>{HtmlReactParser(userInput)}</StyledUserGeneratedHtmlText>
 * </pre>
 */
export const generalHtmlStyles = (theme: Theme): CSSObject => ({
    "& h1": theme.typography.h1 as CSSObject,
    "& h2": theme.typography.h2 as CSSObject,
    "& h3": theme.typography.h3 as CSSObject,
    "& h4": theme.typography.h4 as CSSObject,
    "& h5": theme.typography.h5 as CSSObject,
    "& h6": theme.typography.h6 as CSSObject,
    "& a": {
        /** these values are copied from the ui-kit's Link component. */
        fontWeight: theme.typography.fontWeightBold,
        color: theme.kaltura.palette.tone1,
        textDecorationColor: theme.kaltura.palette.tone1,
        "&:hover": {
            color: theme.kaltura.palette.tone2,
            textDecoration: "none",
        },
        "&:active": {
            color: theme.kaltura.palette.tone2,
        },
        "&:visited": {
            color: theme.kaltura.palette.tone2,
        },
        "&:focus-visible": VisibleFocusStyle,
    },
    "& img": {
        // Large images shouldn't blow up the container
        maxWidth: "100%",
        // Make sure that the image maintains aspect ratio when there are "width" and "height" attributes on the <img> tag,
        // and the max-width rule above changes the width
        height: "auto",
    },
});

/**
 * Addition to generalHtmlStyles() for components that are aligned to center,
 * providing fixes for the list bullet points
 */
export const generalHtmlStylesAlignCenter: CSSObject = {
    "ul, ol": {
        display: "inline-block", //make the UL element inline-block, ensures that it does not take up 100% of the width, allowing it to be centered within the parent
        textAlign: "start", // resets the text alignment to follow the natural reading direction of the text
        margin: "0 auto", // centers the UL element within its parent
        listStylePosition: "inside", // ensures that the list markers are inside the content box of the UL/OL element
    },
};
